import { useMatchMedia } from '@udemy/hooks';

// Given a set of media query results for min-width queries against each layout range, return the
// layout range that the viewport is in.
const rangeFromMinWidthQueries = queryResults => {
  const descendingKeys = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs', 'xxs']; // Query results are null on the server and on the initial render client-side

  if (descendingKeys.some(key => queryResults[key] === null)) {
    return null;
  } // From widest to most narrow, return the first true value


  const range = descendingKeys.find(key => queryResults[key]); // If no range is true, return the fallback, most narrow range

  return range !== null && range !== void 0 ? range : 'xxxs';
};
/**
 * Hook to fetch information about the browser's viewport.
 *
 * @returns an object with properties about the viewport.
 *
 * @remarks
 *
 * `range`: the layout range of the viewport. `range` will be `null` on server-side calls
 * and on the initial render client-side. Thereafter it will be a valid `LayoutRange`.
 *
 * In tests, mock the media queries that this hook uses with
 * `viewportRangeMediaQueriesMock`.
 *
 * @see {@link LayoutRange}
 * @see utility functions for working with ranges, e.g., {@link withinOrWiderThan}
 *
 * @example
 *
 * ```tsx
 * import {useViewport} from '@udemy/react-layout-components';
 *
 * const Foo = () => {
 *    const {range} = useViewport();
 *
 *    // Note: range is `null` server-side and on the initial render client-side
 *    const isWideViewport = range && withinOrWiderThan(range, 'lg');
 *
 *    return <div>{isWideViewport ? 'Wide' : 'Narrow'}</div>;
 * };
 * ```
 */


export const useViewport = () => {
  const minWidthResults = {
    xxs: useMatchMedia('xxs-min'),
    xs: useMatchMedia('xs-min'),
    sm: useMatchMedia('sm-min'),
    md: useMatchMedia('md-min'),
    lg: useMatchMedia('lg-min'),
    xl: useMatchMedia('xl-min'),
    xxl: useMatchMedia('xxl-min')
  };
  return {
    range: rangeFromMinWidthQueries(minWidthResults)
  };
};
//# sourceMappingURL=use-viewport.js.map